/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconEye,
  IconPlus,
  IconTag,
  IconSearch,
  IconX,
  IconStack,
  IconFileText,
  IconRefresh,
  IconPencil,
  IconUpload,
} from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ToggleButton from "../../../components/ToggleButton";
import {
  useFetchProductItemData,
  useUpdateProductItemStatus,
} from "../../../apis/productApis";
import { useEffect, useState, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { useSelector } from "react-redux";
import { handleDownload } from "../../../utilities/downloadHelper";
import React from "react";
import { useForm } from "react-hook-form";
import {
  useItemPromoCreateMutation,
  usePromoUpdateMutation,
} from "../../../apis/promoApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import CreatePromoModal from "./CreatePromoModal";
import AddItem from "../add-item";
import ProductFetch from "./ProductFetch";
import ItemBulkUploadModals from "./ItemBulkUploadModals";
import ItemBulkResponseModals from "./ItemBulkResponseModals";
import { selectClientPermissions } from "../../../features/auth/authSlice";
import { usePermission } from "../../../hooks/usePermissionTabs";
import formatDate from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";

type ProductData = {
  id: string;
};

const hasPermission = (
  permissions: any,
  modules: string[] | undefined,
  operation: string | undefined,
): boolean => {
  if (!permissions || !modules || !operation) return true;
  return modules.some((mod) =>
    permissions.some(
      (p: any) => p.module === mod && p.operations.includes(operation),
    ),
  );
};

export default function ItemTab() {
  const { t, language } = useLanguage();
  const permissions = useSelector(selectClientPermissions);
  const canFetchProduct = hasPermission(permissions, ["product_fetch"], "read");
  const { allowed: canCreateProduct } = usePermission("products", "create");
  const { allowed: canUpdateProduct } = usePermission("products", "update");
  const { allowed: canCreatePromo } = usePermission("promo", "create");
  const [isProductFetchOpen, setIsProductFetchOpen] = useState<boolean>(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState<boolean>(false);
  const [isBulkResponseOpen, setIsBulkResponseOpen] = useState<boolean>(false);
  const [itemBulkResponse, setItemBulkResponse] = useState<any>(null);

  const addItemRef = useRef<{ submit: () => void }>(null);

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<ProductData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);
  const expandedIdsRef = React.useRef<Set<string>>(new Set());
  const [, forceUpdate] = useState(0);
  const [activeSubTabs, setActiveSubTabs] = useState<Record<string, string>>(
    {},
  );

  const debouncedSearchFor = useDebounce(searchFor, 550);
  const {
    register: registerPromo,
    handleSubmit: handleSubmitPromo,
    formState: { errors: promoErrors },
    clearErrors: clearPromoErrors,
    reset: resetPromo,
    setValue: setPromoValue,
    watch: watchPromo,
  } = useForm();

  // States for Promo Modal
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<any>(null);
  const [promoModalMode, setPromoModalMode] = useState<"add" | "view">("add");

  // States for Add Product Side panel
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    undefined,
  );

  // Promo queries & mutations
  const { data: clients } = useMerchantAllListData();
  const { mutate: createPromo, isPending: promoCreating } =
    useItemPromoCreateMutation();
  const { mutate: updatePromo, isPending: promoUpdating } =
    usePromoUpdateMutation();

  const handleAddPromo = () => {
    setSelectedPromo(null);
    resetPromo();
    setPromoModalMode("add");
    setIsAddProductOpen(false);
    setIsProductFetchOpen(false);
    setIsBulkUploadOpen(false);
    setIsBulkResponseOpen(false);
    setIsPromoOpen(true);
  };

  const handleViewPromo = (promo: any) => {
    setSelectedPromo(promo);
    setPromoModalMode("view");
    setIsAddProductOpen(false);
    setIsProductFetchOpen(false);
    setIsBulkUploadOpen(false);
    setIsBulkResponseOpen(false);
    setIsPromoOpen(true);
    resetPromo();
    setPromoValue("toClient", promo.clientId);
    setPromoValue("startEndDate", promo.startEndDate);
    setPromoValue("promoCode", promo.promoCode);
    if (promo.products && promo.products.length > 0) {
      setPromoValue(
        "products",
        promo.products.map((product: any) => ({
          productId: product.productId || "",
          productName: product.productName || "",
          quantity: product.quantity || "",
        })),
      );
    } else {
      setPromoValue("products", [
        { productId: "", productName: "", quantity: "" },
      ]);
    }
  };

  const onSubmitPromo = (values: any, mode: "add" | "edit") => {
    const payload = {
      clientType: values.clientType,
      toClient: values.toClient,
      startEndDate: values.startEndDate,
      promoCode: values.promoCode,
      productAndQuantity: watchPromo("products")
        ?.filter((product: any) => product?.productId)
        ?.map((productData: any) => {
          return {
            ...productData,
            quantity: parseInt(productData?.quantity),
          };
        }),
    };

    if (mode === "edit" && selectedPromo) {
      updatePromo(
        { id: selectedPromo.id, ...payload },
        {
          onSuccess: () => {
            toast.success(t("Promo updated successfully"));
            resetPromo();
            setIsPromoOpen(false);
            refetch();
          },
          onError: (err: any) => toast.error(t(err?.message || "Update failed")),
        },
      );
    } else {
      createPromo(payload, {
        onSuccess: () => {
          toast.success(t("Promo created successfully"));
          resetPromo();
          setIsPromoOpen(false);
          refetch();
        },
        onError: (err: any) => toast.error(t(err?.message || "Create failed")),
      });
    }
  };

  const { isLoading, isFetching, error, refetch } = useFetchProductItemData(
    pageNo,
    limit,
    debouncedSearchFor,
  );

  const data = useSelector((state: any) => state?.product?.data);

  const toggleRow = (id: string) => {
    if (expandedIdsRef.current.has(id)) {
      expandedIdsRef.current.delete(id);
    } else {
      expandedIdsRef.current.add(id);
    }
    forceUpdate((n) => n + 1);
  };

  const { mutate: updateProductStatus, isPending: statusPending } =
    useUpdateProductItemStatus();

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = async () => {
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateProductStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Status updated successfully"));
          setActionModal(false);
        },
        onError: (error) => {
          toast.error(t(`${error}`));
        },
      },
    );
  };

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.productItems) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.productItems.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.productItems, selectedRows]);

  const [columnsData, setColumnsData] = useState([
    { text: "Items", value: "itemName", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    { text: "Category", value: "category", isShow: true },
    { text: "Storage Type", value: "storageType", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "Hs Code", value: "hsCode", isShow: true },
    { text: "Dimensions", value: "dimensions", isShow: true },
    { text: "Weight", value: "weight", isShow: true },
    { text: "Created By", value: "createdByName", isShow: true },
    { text: "Created At", value: "createdAt", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Item Name": c.itemName,
    SKU: c.sku,
    Category: c.category,
    Warehouse: c.warehouse,
    "Storage Type": c.storageType,
    Capacity: c.capacity,
    Weight: c.weight,
    "Hs Code": c.hsCode,
    Status: c.status,
  });

  const isAnySidePanelOpen =
    isPromoOpen ||
    isAddProductOpen ||
    isProductFetchOpen ||
    isBulkUploadOpen ||
    isBulkResponseOpen;

  const gridColsClass = isAnySidePanelOpen
    ? "grid-cols-[40px_56px_1.2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr]"
    : "grid-cols-[40px_56px_1.2fr_1fr_1fr_1fr_0.8fr_0.8fr_0.8fr_1fr_0.8fr_1fr_auto]";

  return (
    <>
      <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div
          className={
            isAnySidePanelOpen ? "col-span-12 xl:col-span-7" : "col-span-12"
          }
        >
          <div
            className="bg-white rounded-3xl overflow-visible mb-6"
          >
            <div className="px-6 py-1.5 flex flex-col md:flex-row md:items-center justify-between gap-4 select-none">
              {/* Left side: Brand badge */}
              <div className="flex flex-wrap items-center gap-3"></div>

              {/* Right side: Controls */}
              <div className="flex items-center flex-wrap gap-2">
                {/* Sliding Search */}
                <div className="relative flex items-center">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-[#212121] focus:outline-none flex-shrink-0"
                      title="Search"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => {
                            setSearchFor(e.target.value);
                            setPageNo(1);
                          }}
                          placeholder={t("Search products...")}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-sm text-[#212121] placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title="Clear & Close"
                        >
                          <IconX size={16} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Download Excel */}
                <button
                  onClick={() =>
                    handleDownload({
                      selectedRows,
                      allSelectedData,
                      setSelectedRows,
                      setAllSelectedData,
                      showCheckboxes,
                      setShowCheckboxes,
                      setColumnsData,
                      mapDataToExport,
                      fileName: "product_list.xlsx",
                    })
                  }
                  className={`w-8 h-8 flex items-center justify-center border border-slate-200 bg-white text-[#212121] hover:bg-slate-50 transition-all duration-150 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                    showCheckboxes && selectedRows.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  title="Download Excel"
                  disabled={showCheckboxes && selectedRows.length === 0}
                >
                  <IconDownload stroke={1.5} size={17} />
                </button>

                {/* Entries Selector */}
                <EntriesSelector
                  value={limit}
                  options={[10, 20, 30, 50]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />

                {/* Columns Filter */}
                <ColumnsFilter
                  columnsData={columnsData.filter(
                    (c) => c.value !== "checkbox",
                  )}
                  setHeadings={setHeadings}
                />

                {/* Add Product */}
                {canCreateProduct && (
                  <button
                    onClick={() => {
                      setIsPromoOpen(false);
                      setIsProductFetchOpen(false);
                      setIsBulkUploadOpen(false);
                      setIsBulkResponseOpen(false);
                      setEditingProductId(undefined);
                      setIsAddProductOpen(true);
                    }}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                      isAddProductOpen
                        ? "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                        : "bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30]"
                    }`}
                    title="Add Item"
                  >
                    <IconPlus stroke={1.5} size={17} />
                  </button>
                )}
              </div>
            </div>

            <div dir={language === "ar" ? "rtl" : "ltr"}>
              {data?.productItems?.length ? (
                <>
                  {/* Header Row with Grid */}
                  <div
                    className={`grid ${gridColsClass} items-center gap-2 border-b border-[#ECECEC] pb-2 px-4 text-xs font-semibold text-[#212121] uppercase`}
                  >
                    {isAnySidePanelOpen ? (
                      <>
                        <div className="col-span-1 text-center"> </div>
                        <div className="col-span-1 text-center">{t("IMAGE")}</div>
                        <div className="col-span-1 text-start">{t("NAME")}</div>
                        <div className="col-span-1 text-start">{t("SKU")}</div>
                        <div className="col-span-1 text-start">{t("CATEGORY")}</div>
                        <div className="col-span-1 text-start">{t("STORAGE")}</div>
                        <div className="col-span-1 text-start">{t("CAPACITY")}</div>
                        <div className="col-span-1 text-start">{t("HS CODE")}</div>
                        <div className="col-span-1 text-start">{t("DIMENSIONS")}</div>
                        <div className="col-span-1 text-start">{t("WEIGHT")}</div>
                      </>
                    ) : (
                      <>
                        <div className="col-span-1 text-center"> </div>
                        <div className="col-span-1 text-center">{t("IMAGE")}</div>
                        <div className="col-span-1 text-start">{t("NAME")}</div>
                        <div className="col-span-1 text-start">{t("SKU")}</div>
                        <div className="col-span-1 text-start">{t("CATEGORY")}</div>
                        <div className="col-span-1 text-start">{t("STORAGE")}</div>
                        <div className="col-span-1 text-start">{t("CAPACITY")}</div>
                        <div className="col-span-1 text-start">{t("HS CODE")}</div>
                        <div className="col-span-1 text-start">{t("DIMENSIONS")}</div>
                        <div className="col-span-1 text-start">{t("WEIGHT")}</div>
                        <div className="col-span-1 text-start">{t("CREATED BY")}</div>
                        <div className="col-span-1 text-start">{t("CREATED AT")}</div>
                        <div className="col-span-1 text-start">{t("STATUS")}</div>
                      </>
                    )}
                  </div>

                  {/* Product Rows */}
                  {data?.productItems?.map((item: any) => {
                    const expanded = expandedIdsRef.current.has(item.id);
                    let capacity = 0;
                    let storageType = "-";
                    try {
                      let storageArray: any[] = [];
                      if (Array.isArray(item.storage)) {
                        storageArray = item.storage.map((s: any) =>
                          typeof s === "string" ? JSON.parse(s) : s,
                        );
                      } else if (typeof item.storage === "string") {
                        storageArray = [JSON.parse(item.storage)];
                      } else {
                        storageArray = [item.storage];
                      }
                      capacity = storageArray.reduce(
                        (sum: number, s: any) => sum + Number(s.capacity || 0),
                        0,
                      );
                      if (storageArray.length > 0) {
                        storageType =
                          storageArray
                            .map((s) => t(s.type || s.storageType || ""))
                            .filter(Boolean)
                            .join(", ") || "-";
                      }
                    } catch {
                      capacity = 0;
                    }

                    return (
                      <div
                        key={item.id}
                        className="bg-white border border-[#ECECEC] rounded-2xl p-4 shadow-sm hover:shadow-md transition-all"
                      >
                        {/* Main Row */}
                        <div
                          className={`grid ${gridColsClass} items-center gap-2 cursor-pointer text-sm text-[#212121]`}
                          onClick={() => toggleRow(item.id)}
                        >
                          {/* Expand button */}
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRow(item.id);
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#ECECEC] bg-[#F7F6F2] hover:bg-gray-100 transition"
                            >
                              {expanded ? (
                                <IconChevronUp
                                  size={16}
                                  className="text-[#404040]"
                                />
                              ) : (
                                <IconChevronDown
                                  size={16}
                                  className="text-[#404040]"
                                />
                              )}
                            </button>
                          </div>

                          {/* Image */}
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-[#ECECEC] flex-shrink-0">
                            <img
                              src={item.image || "/img/product-placeholder.png"}
                              alt={item.itemName}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Name */}
                          <div className="truncate font-medium text-start">
                            {item.itemName}
                          </div>

                          {/* SKU */}
                          <div className="text-xs truncate text-start">{item.sku}</div>

                          {/* Category */}
                          <div className="text-xs text-start">{item.category}</div>

                          {/* Storage Type */}
                          <div className="text-xs text-start">{storageType}</div>

                          {/* Capacity */}
                          <div className="text-xs text-start">{capacity}</div>

                          {/* Hs Code */}
                          <div className="text-xs text-start">{item.hsCode}</div>

                          {/* Dimensions */}
                          <div className="text-xs text-start">
                            {[item.length, item.width, item.height]
                              .filter((v) => v !== null && v !== undefined)
                              .join(" × ")}
                          </div>

                          {/* Weight */}
                          <div className="text-xs text-start">{item.weight}</div>

                          {/* Only show these columns when side panel is closed */}
                          {!isAnySidePanelOpen && (
                            <>
                              {/* Created By */}
                              <div className="text-xs text-start">
                                {item.createdByName}
                              </div>

                              {/* Created At */}
                              <div className="text-xs text-start">
                                {formatDate(item.createdAt)}
                              </div>

                              {/* Status */}
                              <div className="text-start">
                                <button
                                  disabled={!canUpdateProduct}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(item);
                                  }}
                                  className={`px-3 py-1 rounded-full border text-[11px] font-semibold capitalize transition ${
                                    item.status === "ACTIVE"
                                      ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                      : "bg-red-50 text-red-600 border-red-300 hover:bg-red-100"
                                  } ${!canUpdateProduct ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                  {item.status ? t(item.status.toLowerCase()) : ""}
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Expanded Content */}
                        {expanded && (
                          <div className="mt-4 border-t border-[#ECECEC] pt-4">
                            {(() => {
                              const activeTab =
                                activeSubTabs[item.id] || "variants";
                              const subTabs = [
                                {
                                  id: "variants",
                                  label: `${t("Variants")} (${item.variants?.length || 0})`,
                                  icon: (
                                    <IconStack size={14} className="mr-1.5" />
                                  ),
                                },
                                {
                                  id: "promotions",
                                  label: `${t("Promotions")} (${item.promos?.length || 0})`,
                                  icon: (
                                    <IconTag size={14} className="mr-1.5" />
                                  ),
                                },
                                {
                                  id: "terms",
                                  label: t("Terms & Conditions"),
                                  icon: (
                                    <IconFileText
                                      size={14}
                                      className="mr-1.5"
                                    />
                                  ),
                                },
                              ];

                              return (
                                <>
                                  <div className="flex justify-between items-center">
                                    <div className="flex flex-wrap items-center gap-2 mb-4 pb-2 border-b border-[#ECECEC] select-none">
                                      {subTabs.map((tab) => {
                                        const isActive = activeTab === tab.id;
                                        return (
                                          <button
                                            key={tab.id}
                                            onClick={() =>
                                              setActiveSubTabs((prev) => ({
                                                ...prev,
                                                [item.id]: tab.id,
                                              }))
                                            }
                                            className={`flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border transition duration-150 ${
                                              isActive
                                                ? "bg-amber-50 border-amber-300 text-amber-700 font-bold"
                                                : "bg-transparent border-transparent text-[#8A8A8A] hover:text-[#212121]"
                                            }`}
                                          >
                                            {tab.icon}
                                            <span>{tab.label}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                    <button
                                      onClick={() => {
                                        setIsPromoOpen(false);
                                        setIsProductFetchOpen(false);
                                        setEditingProductId(item.id);
                                        setIsAddProductOpen(true);
                                      }}
                                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#ECECEC] bg-white hover:bg-[#F9F9F9] transition"
                                      title={
                                        canUpdateProduct
                                          ? "Edit Details"
                                          : "View Details"
                                      }
                                    >
                                      {canUpdateProduct ? (
                                        <IconPencil
                                          size={18}
                                          className="text-[#404040]"
                                        />
                                      ) : (
                                        <IconEye
                                          size={18}
                                          className="text-[#404040]"
                                        />
                                      )}
                                    </button>
                                  </div>

                                  <div>
                                    {activeTab === "variants" && (
                                      <div>
                                        {item.variants &&
                                        item.variants.length > 0 ? (
                                          <div className="overflow-x-auto rounded-xl border border-[#ECECEC] bg-white">
                                            <table className="min-w-full divide-y divide-[#ECECEC] text-sm">
                                              <thead className="bg-[#F7F6F2] text-xs font-semibold uppercase text-[#8A8A8A]">
                                                <tr>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Name")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Colour")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("SKU / CODE")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("HS Code")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Storage")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Capacity")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Size")}
                                                  </th>
                                                  <th className="px-4 py-2 text-left">
                                                    {t("Weight")}
                                                  </th>
                                                  <th className="px-4 py-2 text-center">
                                                    {t("Status")}
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-[#ECECEC] bg-white">
                                                {item.variants.map(
                                                  (
                                                    variant: any,
                                                    idx: number,
                                                  ) => (
                                                    <tr
                                                      key={variant.id}
                                                      className={
                                                        idx % 2 === 0
                                                          ? "bg-white"
                                                          : "bg-[#FAFAFA]"
                                                      }
                                                    >
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {t("Iphone")}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {variant.optionValue ||
                                                          variant.variantName ||
                                                          "-"}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {variant.sku || "-"}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {variant.hsCode || "-"}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {storageType}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        6
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {t("Size")}
                                                      </td>
                                                      <td className="px-4 py-2 text-xs text-gray-600">
                                                        {variant.weight || "-"}
                                                      </td>
                                                      <td className="px-4 py-2 flex justify-center items-center text-center">
                                                        <ToggleButton
                                                          checked={
                                                            variant.status ===
                                                            "ACTIVE"
                                                          }
                                                        />
                                                      </td>
                                                    </tr>
                                                  ),
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <div className="p-4 text-center border border-dashed border-[#ECECEC] rounded-xl text-xs text-[#8A8A8A] bg-white">
                                            {t("Variants not enabled for this SKU hierarchy.")}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {activeTab === "promotions" && (
                                      <div>
                                        {(() => {
                                          const activePromos =
                                            item.promos || [];
                                          return activePromos.length > 0 ? (
                                            <div className="overflow-x-auto rounded-xl border border-[#ECECEC] bg-white">
                                              <table className="min-w-full divide-y divide-[#ECECEC] text-sm">
                                                <thead className="bg-[#F7F6F2] text-xs font-semibold uppercase text-[#8A8A8A]">
                                                  <tr>
                                                    <th className="px-4 py-2 text-left">
                                                      {t("PROMO SKU")}
                                                    </th>
                                                    <th className="px-4 py-2 text-left">
                                                      {t("ALLOCATED QTY")}
                                                    </th>
                                                    <th className="px-4 py-2 text-left">
                                                      {t("DURATION")}
                                                    </th>
                                                    <th className="px-4 py-2 text-center">
                                                      {t("ACTION")}
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody className="divide-y divide-[#ECECEC] bg-white">
                                                  {activePromos.map(
                                                    (promo: any) => {
                                                      const matchingProduct =
                                                        promo.products?.find(
                                                          (prod: any) =>
                                                            prod.productId ===
                                                              item.id ||
                                                            prod.productId ===
                                                              item.sku,
                                                        );
                                                      return (
                                                        <tr
                                                          key={promo.id}
                                                          onClick={() =>
                                                            handleViewPromo(
                                                              promo,
                                                            )
                                                          }
                                                          className="hover:bg-slate-50/50 transition cursor-pointer"
                                                        >
                                                          <td className="px-4 py-2 font-semibold text-amber-600">
                                                            {promo.promoCode}
                                                          </td>
                                                          <td className="px-4 py-2 text-[#212121]">
                                                            {matchingProduct?.quantity ||
                                                              0}{" "}
                                                            {t("units")}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-[#8A8A8A]">
                                                            {promo.startEndDate
                                                              ? promo.startEndDate.replace(
                                                                  " - ",
                                                                  ` ${t("to")} `,
                                                                )
                                                              : "-"}
                                                          </td>
                                                          <td className="px-4 py-2 flex justify-center">
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleViewPromo(
                                                                  promo,
                                                                );
                                                              }}
                                                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECECEC] bg-white hover:bg-[#F9F9F9] transition"
                                                              title="View Promotion"
                                                            >
                                                              <IconEye
                                                                size={16}
                                                                className="text-[#404040]"
                                                              />
                                                            </button>
                                                          </td>
                                                        </tr>
                                                      );
                                                    },
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : (
                                            <div className="p-4 text-center border border-dashed border-[#ECECEC] rounded-xl text-xs text-[#8A8A8A] bg-white">
                                              {t("No active promotions for this product.")}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}

                                    {activeTab === "terms" && (
                                      <div className="bg-[#F7F6F2]/40 rounded-2xl p-5 border border-[#ECECEC]">
                                        <h4 className="text-xs font-bold text-[#8A8A8A] uppercase tracking-wider mb-3">
                                          {t("Terms & Conditions")}
                                        </h4>
                                        <div className="bg-white p-4 rounded-xl border border-[#ECECEC] max-h-60 overflow-y-auto">
                                          <span className="text-xs font-bold text-[#8A8A8A] uppercase">
                                            {t("Policy Content")}
                                          </span>
                                          <p className="text-sm text-[#404040] mt-1 leading-relaxed whitespace-pre-line">
                                            {item.termsAndConditions ||
                                              t("No terms and conditions configured for this product.")}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <NoDataFound />
              )}
            </div>
          </div>
        </div>
        <div className="col-span-12 xl:col-span-5 space-y-4  xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
          {isAnySidePanelOpen && (
            <div>
              <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden bg-white">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 ">
                  <div className="flex  items-center gap-4">
                    <h2 className="text-lg font-semibold text-[#212121]/60 truncate">
                      {isAddProductOpen
                        ? editingProductId
                          ? t("Edit Item")
                          : t("Add Item")
                        : isPromoOpen
                          ? promoModalMode === "add"
                            ? t("Create Promo")
                            : t("View Promo")
                          : isProductFetchOpen
                            ? t("Product Fetch")
                            : isBulkUploadOpen
                              ? t("Bulk Upload")
                              : ""}
                    </h2>
                    <div className="flex items-center gap-4">
                      {/* Bulk Upload Item */}
                      {canCreateProduct && (
                        <button
                          onClick={() => {
                            setIsPromoOpen(false);
                            setIsProductFetchOpen(false);
                            setIsAddProductOpen(false);
                            setIsBulkUploadOpen(true);
                            setIsBulkResponseOpen(false);
                          }}
                          className="flex items-center justify-center p-1.5 border border-slate-200 bg-white text-[#212121] hover:bg-slate-50 transition duration-150 h-8 w-8 rounded-lg shadow-sm"
                          title="Bulk Upload Item"
                        >
                          <IconUpload stroke={1.5} size={18} />
                        </button>
                      )}

                      {/* Create Promo */}
                      {canCreatePromo && (
                        <button
                          onClick={handleAddPromo}
                          className="flex items-center justify-center p-1.5 border border-slate-200 bg-white text-[#212121] hover:bg-slate-50 transition duration-150 h-8 w-8 rounded-lg shadow-sm"
                          title="Create Promo"
                        >
                          <IconTag stroke={1.5} size={18} />
                        </button>
                      )}

                      {/* Product Fetch */}
                      {canFetchProduct && (
                        <button
                          onClick={() => {
                            setIsPromoOpen(false);
                            setIsAddProductOpen(false);
                            setIsBulkUploadOpen(false);
                            setIsBulkResponseOpen(false);
                            setIsProductFetchOpen(true);
                          }}
                          className="flex items-center justify-center p-1.5 border border-slate-200 bg-white text-[#212121] hover:bg-slate-50 transition duration-150 h-8 w-8 rounded-lg shadow-sm"
                          title="Product Fetch"
                        >
                          <IconRefresh stroke={1.5} size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAddProductOpen && (
                      <>
                        {editingProductId ? (
                          canUpdateProduct && (
                            <button
                              onClick={() => addItemRef.current?.submit()}
                              className="px-4 py-1.5 text-xs bg-[#FBD037] text-[#212121] rounded-lg"
                            >
                              {t("Update")}
                            </button>
                          )
                        ) : (
                          canCreateProduct && (
                            <button
                              onClick={() => addItemRef.current?.submit()}
                              className="px-4 py-1.5 text-xs bg-[#FBD037] text-[#212121] rounded-lg"
                            >
                              {t("Save")}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => setIsAddProductOpen(false)}
                          className="p-1.5 rounded-lg border border-[#D2D2D2]"
                        >
                          <IconX size={18} />
                        </button>
                      </>
                    )}

                    {/* Add similar for other modals if needed */}
                  </div>
                </div>

                {isAddProductOpen && (
                  <AddItem
                    // itemId={editingProductId}
                    // onClose={() => setIsAddProductOpen(false)}
                    // onSuccess={() => {
                    //   setIsAddProductOpen(false);
                    //   refetch();
                    // }}
                    // inline={true}
                    ref={addItemRef}
                    hideHeader={true}
                    itemId={editingProductId}
                    onClose={() => setIsAddProductOpen(false)}
                    onSuccess={() => {
                      setIsAddProductOpen(false);
                      refetch();
                    }}
                    inline={true}
                  />
                )}
                {isPromoOpen && (
                  <CreatePromoModal
                    open={isPromoOpen}
                    onClose={() => setIsPromoOpen(false)}
                    setValue={setPromoValue}
                    watch={watchPromo}
                    register={registerPromo}
                    errors={promoErrors}
                    clearErrors={clearPromoErrors}
                    onSubmit={handleSubmitPromo((vals) =>
                      onSubmitPromo(
                        vals,
                        promoModalMode === "add" ? "add" : "edit",
                      ),
                    )}
                    loading={promoCreating || promoUpdating}
                    mode={promoModalMode}
                    setMode={setPromoModalMode}
                    clients={clients}
                    inline={true}
                  />
                )}
                {isProductFetchOpen && (
                  <div>
                    <ProductFetch
                      setActiveTab={() => {
                        setIsProductFetchOpen(false);
                        refetch();
                      }}
                    />
                  </div>
                )}

                {isBulkUploadOpen && (
                  <div>
                    <ItemBulkUploadModals
                      onClose={() => setIsBulkUploadOpen(false)}
                      onResponseOpen={() => setIsBulkResponseOpen(true)}
                      setItemBulkResponse={setItemBulkResponse}
                      refetchTable={refetch}
                    />
                  </div>
                )}

                {isBulkResponseOpen && itemBulkResponse && (
                  <div>
                    <ItemBulkResponseModals
                      onClose={() => {
                        setIsBulkResponseOpen(false);
                        setItemBulkResponse(null);
                      }}
                      data={itemBulkResponse}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {data?.totalPages > 1 && (
          <div className=" col-span-12">
            <Pagination
              totalPages={data.totalPages}
              currentPage={pageNo}
              fetchData={setPageNo}
              totalCount={data.total}
              itemLabel="manifests"
            />
          </div>
        )}
      </div>

      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={
          selectedItem?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </>
  );
}
